import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { api } from "@/lib/api";
import { captureAttribution, trackEvent, getConsent } from "@/lib/tracking";
import WhatsAppButton from "@/components/WhatsAppButton";

export const ConfigContext = createContext(null);
export const useConfig = () => useContext(ConfigContext);

const NAV = [
  { to: "/cocktail-catering", label: "Cocktail" },
  { to: "/barista-catering", label: "Barista" },
  { to: "/messe-catering", label: "Messe" },
  { to: "/hochzeitscatering", label: "Hochzeit" },
  { to: "/personal", label: "Personal" },
  { to: "/cocktailkurse", label: "Kurse" },
];

export default function Layout() {
  const [config, setConfig] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
  }, []);

  useEffect(() => {
    if (getConsent()?.marketing) captureAttribution();
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const tel = config?.phone_e164 ? `tel:${config.phone_e164}` : null;
  const mail = config?.email ? `mailto:${config.email}` : null;

  return (
    <ConfigContext.Provider value={config}>
      <div className="min-h-screen bg-background text-foreground">
        <a href="#hauptinhalt" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded">
          Zum Inhalt springen
        </a>

        <header className="sticky top-0 z-50 bg-[#0e0e11]/90 backdrop-blur-md border-b border-[#24232a]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center shrink-0" data-testid="header-logo" aria-label="Mabuhay Cocktail Service – zur Startseite">
              <img src="/img/logo-420.webp" alt="Mabuhay Cocktail Service" width="420" height="257"
                className="h-10 sm:h-12 w-auto" data-testid="header-logo-image" />
            </Link>

            <nav className="hidden lg:flex items-center gap-7 text-xs uppercase tracking-[0.14em] text-[#a6a29d]" aria-label="Hauptnavigation">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className={`hover:text-[#f4efe8] transition-colors ${location.pathname === n.to ? "text-[#d97724]" : ""}`}>
                  {n.label}
                </Link>
              ))}
              <Link to="/referenzen" className="inline-block py-1.5 hover:text-[#f4efe8] transition-colors">Referenzen</Link>
              <Link to="/kontakt" className="inline-block py-1.5 hover:text-[#f4efe8] transition-colors">Kontakt</Link>
            </nav>

            <div className="flex items-center gap-2">
              {tel && (
                <a href={tel} onClick={() => trackEvent("phone_click", { page: location.pathname, position: "header" })}
                  data-testid="header-phone-button"
                  className="hidden md:inline-flex items-center gap-2 px-3 py-2 min-h-[44px] text-xs font-semibold rounded border border-[#32303c] text-[#f4efe8] hover:border-[#d97724] transition-colors">
                  <Phone className="w-4 h-4" aria-hidden="true" />
                  <span className="xl:hidden">Anrufen</span>
                  <span className="hidden xl:inline">{config.phone_display}</span>
                </a>
              )}
              <WhatsAppButton position="header" compact className="hidden sm:inline-flex" />
              <Link to="/angebot" data-testid="header-cta-button"
                onClick={() => trackEvent("cta_click", { page: location.pathname, position: "header" })}
                className="inline-flex items-center px-4 py-2.5 min-h-[44px] bg-[#b35c0f] text-white text-xs font-bold uppercase tracking-wider rounded hover:bg-[#96500c] transition-colors">
                Kostenloses Angebot
              </Link>
              <button type="button" className="lg:hidden p-2 text-[#f4efe8]" aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"} aria-expanded={menuOpen}
                data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)}>
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          {menuOpen && (
            <nav className="lg:hidden border-t border-[#24232a] bg-[#0e0e11] px-4 py-4 flex flex-col gap-3 text-sm uppercase tracking-[0.12em] text-[#a6a29d]" aria-label="Mobile Navigation">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="py-1.5 hover:text-[#f4efe8]">{n.label}</Link>
              ))}
              <Link to="/referenzen" className="py-1.5 hover:text-[#f4efe8]">Referenzen</Link>
              <Link to="/kontakt" className="py-1.5 hover:text-[#f4efe8]">Kontakt</Link>
            </nav>
          )}
        </header>

        <main id="hauptinhalt" className="pb-24 lg:pb-0">
          <Outlet />
        </main>

        <footer className="bg-[#08080a] text-[#8f8a85] py-12 pb-28 lg:pb-12 border-t border-[#1c1a24] text-xs">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-8">
              <div>
                <img src="/img/logo-420.webp" alt="Mabuhay Cocktail Service" width="420" height="257"
                  className="h-12 w-auto mb-2" data-testid="footer-logo-image" />
                <p className="mt-1">Cocktail- & Barista-Catering</p>
                <p className="mt-2 max-w-xs leading-relaxed">
                  {config?.service_area || "Deutschlandweit und europaweit einsetzbar."}
                </p>
              </div>
              <div>
                <p className="font-bold text-[#f4efe8] uppercase font-display text-sm tracking-wider mb-2">Leistungen</p>
                <ul className="space-y-1.5">
                  {NAV.map((n) => (
                    <li key={n.to}><Link to={n.to} className="inline-block py-1.5 hover:text-[#f4efe8] transition-colors">{n.label}</Link></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-bold text-[#f4efe8] uppercase font-display text-sm tracking-wider mb-2">Kontakt</p>
                {mail ? (
                  <a href={mail} onClick={() => trackEvent("email_click", { page: location.pathname, position: "footer" })}
                    data-testid="footer-email-link" className="block hover:text-[#f4efe8] transition-colors">{config.email}</a>
                ) : (
                  <span data-testid="footer-email-disabled" className="block text-[#9a958f]" title="E-Mail-Adresse wird vom Betreiber konfiguriert">E-Mail folgt in Kürze</span>
                )}
                {tel ? (
                  <a href={tel} data-testid="footer-phone-link" className="block mt-1.5 hover:text-[#f4efe8] transition-colors">{config.phone_display}</a>
                ) : (
                  <span className="block mt-1.5 text-[#9a958f]">Telefon folgt in Kürze</span>
                )}
                <Link to="/angebot" className="inline-block mt-3 text-[#d97724] hover:underline">Kostenloses Angebot anfordern</Link>
              </div>
              <div>
                <p className="font-bold text-[#f4efe8] uppercase font-display text-sm tracking-wider mb-2">Rechtliches</p>
                <ul className="space-y-1.5">
                  <li><Link to="/impressum" data-testid="footer-impressum-link" className="inline-block py-1.5 hover:text-[#f4efe8] transition-colors">Impressum</Link></li>
                  <li><Link to="/datenschutz" data-testid="footer-datenschutz-link" className="inline-block py-1.5 hover:text-[#f4efe8] transition-colors">Datenschutz</Link></li>
                  <li>
                    <button type="button" data-testid="footer-cookie-settings-button"
                      onClick={() => window.dispatchEvent(new Event("open-consent-settings"))}
                      className="inline-block py-1.5 hover:text-[#f4efe8] transition-colors">
                      Cookie-Einstellungen
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-10 pt-6 border-t border-[#1c1a24] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p>© {config?.company_legal_name || "Mabuhay Cocktail & Barista Catering"}</p>
              <p>Kostenlos & unverbindlich anfragen – wir melden uns zurück.</p>
            </div>
          </div>
        </footer>

        <WhatsAppFloating />
        <MobileActionBar />
      </div>
    </ConfigContext.Provider>
  );
}

function WhatsAppFloating() {
  const config = useConfig();
  if (!config?.whatsapp_e164) return null;
  return (
    <div className="fixed bottom-20 lg:bottom-6 right-4 z-40 hidden sm:block">
      <div className="flex flex-col items-end gap-1">
        <WhatsAppButton position="floating" large data-testid="whatsapp-floating-button" />
        <p className="text-[11px] text-[#a6a29d] bg-[#0e0e11]/90 px-2 py-1 rounded border border-[#24232a]">
          {config.whatsapp_subline || "Wir melden uns sofort zurück."}
        </p>
      </div>
    </div>
  );
}

function MobileActionBar() {
  const config = useConfig();
  const location = useLocation();
  const tel = config?.phone_e164 ? `tel:${config.phone_e164}` : null;
  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0e0e11] border-t border-[#24232a] p-2.5 flex items-center gap-2 shadow-2xl"
      data-testid="mobile-action-bar">
      {tel && (
        <a href={tel} onClick={() => trackEvent("phone_click", { page: location.pathname, position: "floating" })}
          data-testid="mobile-call-button"
          className="px-3 py-2.5 min-h-[48px] border border-[#32303c] text-[#f4efe8] text-xs font-semibold rounded flex items-center justify-center gap-1.5">
          <Phone className="w-4 h-4" aria-hidden="true" />
          <span>Anrufen</span>
        </a>
      )}
      <WhatsAppButton position="floating" compact />
      <Link to="/angebot" data-testid="mobile-cta-button"
        onClick={() => trackEvent("cta_click", { page: location.pathname, position: "floating" })}
        className="flex-1 py-2.5 min-h-[48px] flex items-center justify-center bg-[#b35c0f] text-white text-xs font-bold uppercase text-center rounded hover:bg-[#96500c] transition-colors">
        Angebot anfordern
      </Link>
    </div>
  );
}
